import React, { useState, useEffect, useCallback } from 'react';
import API_ENDPOINTS from '../config/api';

const TYPE_LABELS = {
  apex_trigger: 'apex trigger',
  validation_rule: 'validation rule',
  workflow_rule: 'workflow rule',
};

const typeLabel = (type) => TYPE_LABELS[type] || (type || '').replace(/_/g, ' ');

const statusInfo = (active) => {
  if (active === true) return { label: 'active', className: 'automation-status-active' };
  if (active === false) return { label: 'inactive', className: 'automation-status-inactive' };
  return { label: 'unknown', className: 'automation-status-unknown' };
};

const detailText = (row) => {
  const detail = row && row.detail;
  if (!detail) return '—';
  if (Array.isArray(detail.events) && detail.events.length > 0) {
    return detail.events.join(', ');
  }
  if (detail.errorMessage) return detail.errorMessage;
  return '—';
};

const AutomationInventory = ({ getAuthHeaders }) => {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_AUTOMATIONS, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to load automations (${response.status})`);
      }
      const data = await response.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load automations');
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScan = async () => {
    setScanning(true);
    setError(null);
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTATION_AUTOMATIONS_SCAN, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Failed to scan automations (${response.status})`);
      }
      const data = await response.json();
      setScanResult(data);
      await fetchRows();
    } catch (err) {
      setError(err.message || 'Failed to scan automations');
    } finally {
      setScanning(false);
    }
  };

  const filteredRows = rows.filter((row) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (row.object_name && row.object_name.toLowerCase().includes(q)) ||
      (row.api_name && row.api_name.toLowerCase().includes(q))
    );
  });

  return (
    <div className="automation-panel">
      <p className="automation-explainer">
        A unified inventory of triggers, validation rules, and workflow rules across your
        org — one place to see what fires, on what object, and whether it's active.
      </p>

      <div className="automation-toolbar glass-card">
        <input
          type="text"
          placeholder="Filter by object or name…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="automation-filter-input"
        />
        <button
          className="automation-scan-btn"
          onClick={handleScan}
          disabled={scanning}
        >
          {scanning ? 'Scanning…' : 'Scan Automations'}
        </button>
      </div>

      {scanResult && (
        <div className="automation-scan-summary">
          {scanResult.count} automations found
          {scanResult.byType &&
            Object.entries(scanResult.byType).map(([type, count]) => (
              <span key={type} className="automation-scan-summary-pill">
                {typeLabel(type)}: {count}
              </span>
            ))}
        </div>
      )}

      {error && <div className="automation-error-banner">{error}</div>}

      {filteredRows.length === 0 ? (
        <div className="automation-empty">
          {rows.length > 0
            ? 'No automations match your filter.'
            : 'No automations scanned yet — run a scan.'}
        </div>
      ) : (
        <div className="automation-table-wrapper glass-card">
          <table className="automation-table">
            <thead>
              <tr>
                <th>Automation</th>
                <th>Type</th>
                <th>Object</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const status = statusInfo(row.active);
                return (
                  <tr key={row.id}>
                    <td className="automation-mono">{row.api_name}</td>
                    <td>
                      <span className="automation-type-chip">{typeLabel(row.type)}</span>
                    </td>
                    <td className="automation-mono">{row.object_name}</td>
                    <td>
                      <span className={`automation-status-badge ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="automation-detail">{detailText(row)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .automation-panel {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .automation-explainer {
    color: var(--gray-400);
    font-size: 0.9rem;
    max-width: 60ch;
  }

  .automation-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem 1.25rem;
  }

  .automation-filter-input {
    flex: 1;
    min-width: 220px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    padding: 0.5rem 0.75rem;
    color: inherit;
    font-size: 0.9rem;
  }

  .automation-scan-btn {
    background: var(--force-cyan);
    color: #06131a;
    border: none;
    border-radius: 6px;
    padding: 0.55rem 1.1rem;
    font-weight: 600;
    cursor: pointer;
  }

  .automation-scan-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .automation-scan-summary {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.6rem;
    color: var(--gray-400);
    font-size: 0.85rem;
  }

  .automation-scan-summary-pill {
    font-family: monospace;
    font-size: 0.8rem;
    background: rgba(0, 229, 255, 0.1);
    color: var(--force-cyan);
    border-radius: 999px;
    padding: 0.2rem 0.6rem;
  }

  .automation-error-banner {
    color: #ff6b6b;
    font-size: 0.85rem;
  }

  .automation-empty {
    color: var(--gray-400);
    font-size: 0.85rem;
  }

  .automation-table-wrapper {
    overflow-x: auto;
    padding: 0.5rem;
  }

  .automation-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
  }

  .automation-table th {
    text-align: left;
    color: var(--gray-400);
    font-weight: 600;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .automation-table td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }

  .automation-mono {
    font-family: monospace;
  }

  .automation-type-chip {
    display: inline-block;
    background: rgba(255, 255, 255, 0.06);
    border-radius: 999px;
    padding: 0.15rem 0.6rem;
    font-size: 0.78rem;
  }

  .automation-detail {
    color: var(--gray-400);
  }

  .automation-status-badge {
    display: inline-block;
    border-radius: 999px;
    padding: 0.15rem 0.6rem;
    font-size: 0.78rem;
    font-weight: 600;
  }

  .automation-status-active {
    background: rgba(46, 204, 113, 0.15);
    color: #2ecc71;
  }

  .automation-status-inactive,
  .automation-status-unknown {
    background: rgba(255, 255, 255, 0.06);
    color: var(--gray-400);
  }
`;

export default AutomationInventory;
